import React, { useState, useCallback, useRef } from 'react';
import {
  Grid, Card, CardHeader, CardContent, Typography, Box, Button, Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LockIcon from '@mui/icons-material/Lock';
import { useActionSettings } from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';

const SettingAddPasswordDocx = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  
  const [sourcePath, setSourcePath] = useState('');
  const [targetPath, setTargetPath] = useState('');
  const [password, setPassword] = useState('');
  
  const sourceRef = useRef(null);
  const targetRef = useRef(null);
  const passwordRef = useRef(null);
  
  const [sourceBrowseDisabled, setSourceBrowseDisabled] = useState(false);

  // Use unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    id, // eslint-disable-line no-unused-vars
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'addpassworddocx',
    onSaveStatusChange,
    customFields: {
      sourceFilePath: '',
      targetPath: 'C:\\output\\{filename}_protected.docx',
      documentPassword: ''
    },
    customComparison: (initial) => {
      return (
        initial.sourceFilePath === sourcePath &&
        initial.targetPath === targetPath &&
        initial.documentPassword === password
      );
    },
    customSaveData: () => ({
      sourceFilePath: sourcePath,
      targetPath: targetPath,
      documentPassword: password
    }),
    loadDataFunction: (data) => {
      console.log('Loading add password docx data:', data);
      
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (e) {
          console.error('Failed to parse data JSON:', e);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }
      
      console.log('Parsed add password docx data:', parsedData);
      
      if (parsedData.sourceFilePath !== undefined) {
        setSourcePath(parsedData.sourceFilePath || '');
      }
      if (parsedData.targetPath !== undefined) {
        setTargetPath(parsedData.targetPath || '');
      }
      if (parsedData.documentPassword !== undefined) {
        setPassword(parsedData.documentPassword || '');
      }
    },
    enableConditions: true
  });

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    onFolderSelected: (folderPath) => {
      setSourcePath(folderPath);
    },
    onFolderBrowserClose: () => {
      setSourceBrowseDisabled(false);
    }
  });

  // Available tags for insertion
  const availableTags = React.useMemo(() => {
    if (!tags || !Array.isArray(tags)) {
      return [];
    }
    
    return tags.map((tag, index) => {
      if (!tag) return null;
      return {
        key: tag.key || tag.value || tag.variable || tag.label,
        label: tag.label || tag.key || tag.value || tag.variable,
        value: tag.value || tag.variable || tag.key || tag.label,
        variable: tag.variable || tag.value || tag.variable || tag.label,
        description: tag.description || '',
        type: tag.type || 'text',
        reply: tag.reply || '',
        tag_type: tag.tag_type || ''
      };
    }).filter(Boolean);
  }, [tags]);

  // Save handler
  const handleSave = useCallback(() => {
    baseHandleSave({
      sourceFilePath: sourcePath,
      targetPath: targetPath,
      documentPassword: password,
      useConditions: true,
      conditions
    });
  }, [baseHandleSave, sourcePath, targetPath, password, conditions]);

  const customDataTags = [
    { 
      key: 'filename', 
      label: 'Filename', 
      value: 'filename', 
      description: 'Original filename without extension' 
    },
    { 
      key: 'current_date', 
      label: 'Current Date', 
      value: 'current_date', 
      description: 'Current date in YYYY-MM-DD format' 
    },
    { 
      key: 'case_id', 
      label: 'Case ID', 
      value: 'case_id', 
      description: 'Current case identifier' 
    }
  ];

  return (
    <ActionSettingsLayout
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('workflow.addpassworddocx', 'Add Password to DOCX')}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={availableTags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
    >
      <Grid container spacing={3}>
        {/* Source File Path */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#5757b0',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                  }}>
                    SOURCE
                  </Box>
                  {t('workflow.source_file_path', 'Source DOCX File')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <Box sx={{
                mb: 1.5,
                border: '1px solid #d0d0e8',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#5757b0',
                },
                '&:focus-within': {
                  borderColor: '#5757b0',
                  boxShadow: '0 0 0 3px rgba(87, 87, 176, 0.1)',
                },
              }}>
                <Box sx={{
                  padding: '12px 16px',
                  minHeight: '28px',
                }}>
                  <TagEditor
                    ref={sourceRef}
                    value={sourcePath}
                    onChange={setSourcePath}
                    placeholder="C:\path\to\source\document.docx"
                    singleLine
                    tags={availableTags}
                  />
                </Box>
              </Box>

              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}>
                <Button
                  size="medium"
                  variant="outlined"
                  onClick={handleBrowseDirectory}
                  disabled={sourceBrowseDisabled}
                  startIcon={<FolderOpenIcon />}
                  sx={{
                    color: '#5757b0',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#f0f0ff',
                      borderColor: '#5757b0',
                      boxShadow: '0 2px 6px rgba(87, 87, 176, 0.15)',
                    },
                    transition: 'all 0.2s ease',
                    borderRadius: '8px',
                  }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {t('workflow.source_file_help', 'Path to the DOCX file that needs password protection')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Target Path */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#5757b0',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                  }}>
                    OUTPUT
                  </Box>
                  {t('workflow.target_path', 'Protected File Output Path')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />              
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <Box sx={{
                mb: 1.5,
                border: '1px solid #d0d0e8',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#5757b0',
                },
                '&:focus-within': {
                  borderColor: '#5757b0',
                  boxShadow: '0 0 0 3px rgba(87, 87, 176, 0.1)',
                },
              }}>
                <Box sx={{
                  padding: '12px 16px',
                  minHeight: '28px',
                }}>
                  <TagEditor
                    ref={targetRef}
                    value={targetPath}
                    onChange={setTargetPath}
                    placeholder={t('workflow.target_path_placeholder', 'Enter output path like C:\\output\\{filename}_protected.docx')}
                    singleLine
                    tags={availableTags}
                  />
                </Box>
              </Box>

              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
              }}>
                {customDataTags.slice(0, 3).map((tag) => (
                  <Chip
                    key={tag.value}
                    label={tag.label}
                    onClick={() => {
                      if (targetRef.current) {
                        targetRef.current.focus();
                        setTimeout(() => {
                          targetRef.current.insertTag(tag.value);
                        }, 10);
                      }
                    }}
                    size="small"
                    sx={{
                      backgroundColor: '#f0f0ff',
                      color: '#5757b0',
                      borderColor: '#5757b030',
                      border: '1px solid',
                      '&:hover': {
                        backgroundColor: '#e0e0ff',
                        boxShadow: '0 2px 6px rgba(87, 87, 176, 0.15)',
                      },
                      fontWeight: 600,
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Password */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#5757b0',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                  }}>
                    <LockIcon sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                    PASSWORD
                  </Box>
                  {t('workflow.document_password', 'Document Password')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <Box sx={{
                mb: 1.5,
                border: '1px solid #d0d0e8',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#5757b0',
                },
                '&:focus-within': {
                  borderColor: '#5757b0',
                  boxShadow: '0 0 0 3px rgba(87, 87, 176, 0.1)',
                },
              }}>
                <Box sx={{
                  padding: '12px 16px',
                  minHeight: '28px',
                }}>
                  <TagEditor
                    ref={passwordRef}
                    value={password}
                    onChange={setPassword}
                    placeholder={t('workflow.password_placeholder', 'Enter password with tags like {case_id}_{current_date}')}
                    singleLine
                    tags={availableTags}
                  />
                </Box>
              </Box>

              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
              }}>
                {customDataTags.slice(0, 3).map((tag) => (
                  <Chip
                    key={tag.value}
                    label={tag.label}
                    onClick={() => {
                      if (passwordRef.current) {
                        passwordRef.current.focus();
                        setTimeout(() => {
                          passwordRef.current.insertTag(tag.value);
                        }, 10);
                      }
                    }}
                    size="small"
                    sx={{
                      backgroundColor: '#f0f0ff',
                      color: '#5757b0',
                      borderColor: '#5757b030',
                      border: '1px solid',
                      '&:hover': {
                        backgroundColor: '#e0e0ff',
                        boxShadow: '0 2px 6px rgba(87, 87, 176, 0.15)',
                      },
                      fontWeight: 600,
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {t('workflow.password_help', 'Password can include tags like {case_id} or {current_date}. Dynamic passwords based on case data.')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {FolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default React.memo(SettingAddPasswordDocx);
